import React, { useEffect, useRef, useState } from "react";
import { X, ChevronLeft } from "lucide-react";
import { C, FONT, SAFE_BOTTOM, glassVars } from "./ui";

// Sheet bawah generik gaya Jawsel (HP-first): handle tarik, header ikon+judul, isi bisa di-scroll.
// Tarik handle ke bawah > 70 px atau ketuk × untuk menutup; onBack (opsional) menampilkan tombol kembali; aksi (opsional) = elemen di kiri tombol ×.
// Viewport visual (iOS/Android): saat keyboard muncul tingginya mengecil → sheet ikut mengecil & footer (kolom input) tetap terlihat di atas keyboard.
function useVisualViewport() {
  const [vv, setVv] = useState({ h: null, top: 0 });
  useEffect(() => {
    const v = window.visualViewport; if (!v) return;
    const f = () => setVv({ h: Math.round(v.height), top: Math.round(v.offsetTop) });
    f(); v.addEventListener("resize", f); v.addEventListener("scroll", f);
    return () => { v.removeEventListener("resize", f); v.removeEventListener("scroll", f); };
  }, []);
  return vv;
}

export function Sheet({ title, sub, icon: Icon, onClose, onBack, children, footer, testid, tall = true, aksi }) {
  const [shown, setShown] = useState(false);
  const vv = useVisualViewport();
  const drag = useRef(null);
  const [dy, setDy] = useState(0);
  useEffect(() => { const t = requestAnimationFrame(() => setShown(true)); return () => cancelAnimationFrame(t); }, []);

  const yOf = e => (e.touches ? e.touches[0].clientY : e.clientY);
  const onStart = e => { drag.current = { y: yOf(e) }; };
  const onMove = e => { if (drag.current) setDy(Math.max(0, yOf(e) - drag.current.y)); };
  const onEnd = () => { if (!drag.current) return; drag.current = null; if (dy > 70) onClose(); else setDy(0); };

  return (
    <div data-testid={testid} role="dialog" aria-label={title}
      style={{ position: "absolute", left: 0, right: 0, top: vv.top, height: vv.h ?? "100%", display: "flex", alignItems: "flex-end", justifyContent: "center", fontFamily: FONT, zIndex: 20 }}>
      <div onClick={onClose} style={{ position: "absolute", inset: 0, background: "rgba(20,20,20,0.28)", opacity: shown ? 1 : 0, transition: "opacity .25s" }} />
      <div style={{
        ...glassVars(false),   // panel putih: selalu variabel warna siang (chip/teks terbaca meski halaman dalam mode malam)
        position: "relative", width: "100%", maxWidth: 560, maxHeight: tall ? "86%" : "60%", display: "flex", flexDirection: "column",
        background: "#fff", borderRadius: "26px 26px 0 0", boxShadow: "0 -12px 40px rgba(20,20,20,0.18)", color: C.ink,
        transform: `translateY(${shown ? dy : 600}px)`, transition: drag.current ? "none" : "transform .3s cubic-bezier(.2,.8,.2,1)",
      }}>
        <div onTouchStart={onStart} onTouchMove={onMove} onTouchEnd={onEnd} onMouseDown={onStart} onMouseMove={onMove} onMouseUp={onEnd} onMouseLeave={onEnd}
          style={{ padding: "8px 18px 10px", cursor: "grab", flex: "none" }}>
          <div style={{ width: 44, height: 5, borderRadius: 99, background: C.line, margin: "0 auto 8px" }} />
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {onBack ? (
              <button data-testid="sheet-back" aria-label="Kembali" onClick={onBack}
                style={{ width: 38, height: 38, borderRadius: 99, border: 0, background: C.cream, color: C.ink, display: "grid", placeItems: "center", cursor: "pointer", flex: "none" }}>
                <ChevronLeft size={18} />
              </button>
            ) : Icon && (
              <span style={{ width: 34, height: 34, borderRadius: 12, background: C.blueSoft, display: "grid", placeItems: "center", flex: "none" }}>
                <Icon size={16} color={C.blue} />
              </span>
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <h3 data-testid="sheet-title" style={{ margin: 0, fontSize: 16, fontWeight: 800, letterSpacing: -0.3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{title}</h3>
              {sub && <p style={{ margin: "1px 0 0", fontSize: 11.5, color: C.sub, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{sub}</p>}
            </div>
            {aksi}
            <button data-testid="sheet-close" aria-label="Tutup" onClick={onClose}
              style={{ width: 36, height: 36, borderRadius: 99, border: 0, background: C.cream, color: C.ink, display: "grid", placeItems: "center", cursor: "pointer", flex: "none" }}>
              <X size={17} />
            </button>
          </div>
        </div>
        <div data-testid="sheet-body" style={{ flex: 1, minHeight: 0, overflowY: "auto", WebkitOverflowScrolling: "touch", padding: `0 18px ${footer ? 12 : `calc(${SAFE_BOTTOM} + 20px)`}` }}>
          {children}
        </div>
        {footer && <div style={{ flex: "none", padding: `8px 18px calc(${SAFE_BOTTOM} + 10px)`, borderTop: `1px solid ${C.line}`, background: "#fff", borderRadius: "0 0 0 0" }}>{footer}</div>}
      </div>
    </div>
  );
}

// Baris kosong/placeholder di dalam sheet
export function Empty({ icon: Icon, text, testid }) {
  return (
    <div data-testid={testid} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, padding: "34px 10px", color: C.sub, textAlign: "center" }}>
      {Icon && <span style={{ width: 52, height: 52, borderRadius: 18, background: C.cream, display: "grid", placeItems: "center" }}><Icon size={22} color={C.sub} /></span>}
      <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.5 }}>{text}</p>
    </div>
  );
}

export const chipStyle = (tone = "blue") => ({
  display: "inline-block", padding: "3px 9px", borderRadius: 99, fontSize: 10.5, fontWeight: 800, letterSpacing: .6, textTransform: "uppercase", whiteSpace: "nowrap",
  // warna lewat CSS variable (diset glassVars siang/malam di root halaman) dengan fallback siang — chip tetap terbaca di kaca gelap
  ...(tone === "green" ? { background: "var(--c-green-bg, #d6f2e2)", color: "var(--c-green, #189a5b)" } : tone === "gray" ? { background: `var(--c-gray-bg, ${C.cream})`, color: `var(--c-gray, ${C.sub})` }
    : tone === "red" ? { background: "var(--c-red-bg, #fee2e2)", color: `var(--c-red, ${C.danger})` } : tone === "amber" ? { background: "var(--c-amber-bg, #fdf1d6)", color: "var(--c-amber, #b7791f)" }
    : { background: `var(--c-blue-bg, ${C.blueSoft})`, color: `var(--c-blue, ${C.blue})` }),
});
